/**
 * Cross-tab project lifecycle channel (SB-2).
 *
 * The cheapest boundary in the whole session-boundary problem was also the only
 * uncovered one: two tabs in the SAME browser share an origin and could always
 * have talked, but `BroadcastChannel` appeared in exactly one place in the
 * client — the tab-guard's fallback election — and nothing ever announced a
 * project's lifecycle. So deleting a project in tab 2 left tab 1 editing it,
 * and tab 1 only found out on its next cloud-sync tick (or never, for a project
 * with no live collab room).
 *
 * Three deliberate constraints, each one a bug this channel could otherwise
 * introduce:
 *
 *  - **No `project-switched`.** Once autosave slots are per-project (SB-3), one
 *    tab switching projects has no bearing on another tab, and broadcasting it
 *    would manufacture exactly the cross-tab coupling this initiative exists to
 *    remove.
 *  - **Principal-scoped.** A message is ignored unless the receiving tab is
 *    signed in as the same account. Two accounts in two profiles do not share a
 *    BroadcastChannel, but one browser signed into different accounts across
 *    tabs does, and a delete in one must not blank the other's unrelated view.
 *  - **Posted only after the server outcome is known.** `confirmAndDeleteProjects`
 *    optimistically drops rows and rolls them back when the API rejects. Posting
 *    optimistically would blank a healthy tab on a delete that then failed.
 *
 * Fully injectable (`channelFactory`) so the state machine is headless-testable,
 * mirroring the `win` injection in core/tab-guard.js.
 */

import { LIFECYCLE_SIGNAL_DELETED } from './project-lifecycle.js';

export const LIFECYCLE_CHANNEL_NAME = 'pb-lifecycle';

/** Message kinds this channel carries. Deliberately small — see the header. */
export const LIFECYCLE_MESSAGE = Object.freeze({
  PROJECT_DELETED: 'project-deleted',
  SIGNED_OUT: 'signed-out'
});

const VALID_KINDS = new Set(Object.values(LIFECYCLE_MESSAGE));

/**
 * @param {object} [opts]
 * @param {(name: string) => object} [opts.channelFactory] - test seam.
 * @param {() => string|null} [opts.getPrincipal] - the signed-in account `sub`
 *   for THIS tab, read at post/receive time (never captured — the tab can sign
 *   in and out while the channel lives).
 * @returns {{post(msg): boolean, subscribe(fn): () => void, close(): void, isSupported(): boolean}}
 */
export function createLifecycleChannel({ channelFactory, getPrincipal = () => null } = {}) {
  const factory =
    channelFactory ||
    (typeof BroadcastChannel === 'function' ? (name) => new BroadcastChannel(name) : null);

  // No BroadcastChannel (older Safari, some embedded webviews, headless tests
  // without a shim): every method becomes an honest no-op. Callers must not
  // depend on delivery — this channel is an accelerator for a signal the
  // cloud-sync tick and the collab close code already provide, never the only
  // path. Same posture as tab-guard's fallback.
  let channel = null;
  if (factory) {
    try {
      channel = factory(LIFECYCLE_CHANNEL_NAME);
    } catch {
      // Expected flow: a browser that exposes the constructor but refuses it in
      // this context (some sandboxed iframes). Degrade, never throw at boot.
      channel = null;
    }
  }

  const listeners = new Set();
  let closed = false;

  function handleMessage(event) {
    if (closed) return;
    const data = event && event.data;
    if (!data || !VALID_KINDS.has(data.kind)) return;
    // Principal scoping. An unsigned tab (null principal) accepts nothing:
    // there is no account whose project could have been deleted.
    const self = getPrincipal();
    if (!self || !data.principal || String(data.principal) !== String(self)) return;
    for (const fn of [...listeners]) {
      try {
        fn(data);
      } catch {
        // A throwing subscriber must not stop the others from being told that
        // their project is gone.
      }
    }
  }

  if (channel) {
    if (typeof channel.addEventListener === 'function') {
      channel.addEventListener('message', handleMessage);
    } else {
      channel.onmessage = handleMessage;
    }
  }

  return {
    isSupported() {
      return !!channel;
    },

    /**
     * Announce a lifecycle fact to the other tabs of this account.
     * @param {{kind: string, projectId?: string, signal?: string}} msg
     * @returns {boolean} true when the message was actually posted
     */
    post(msg) {
      if (closed || !channel) return false;
      const kind = msg && msg.kind;
      if (!VALID_KINDS.has(kind)) throw new Error(`Unknown lifecycle message: ${kind}`);
      const principal = getPrincipal();
      // Nothing to scope the message to means nobody could correctly act on it.
      if (!principal) return false;
      try {
        channel.postMessage({
          kind,
          principal: String(principal),
          projectId: msg.projectId ? String(msg.projectId) : null,
          // The POSTER knows the cause (it performed the delete), so the
          // receiver never has to re-derive it from an ambiguous 404.
          signal: msg.signal || null
        });
        return true;
      } catch {
        // Channel closed underneath us (tab teardown). Nothing to recover.
        return false;
      }
    },

    /** @param {(msg: object) => void} fn @returns {() => void} unsubscribe */
    subscribe(fn) {
      if (typeof fn !== 'function') return () => {};
      listeners.add(fn);
      return () => listeners.delete(fn);
    },

    close() {
      if (closed) return;
      closed = true;
      listeners.clear();
      if (!channel) return;
      try {
        if (typeof channel.removeEventListener === 'function') {
          channel.removeEventListener('message', handleMessage);
        }
        channel.close();
      } catch {
        // Already closed.
      }
      channel = null;
    }
  };
}

// ── App-wide singleton ──────────────────────────────────────────────────────
// Configured once by the shell (which owns `currentUser`), so surfaces like the
// project switcher can post without importing the shell — that import would be
// a cycle, since the shell imports them. Same shape as configureCollabSession /
// initReportError.

let _channel = null;
let _getPrincipal = () => null;

/** @param {{getPrincipal: () => string|null, channelFactory?: Function}} opts */
export function configureLifecycleChannel({ getPrincipal, channelFactory } = {}) {
  if (typeof getPrincipal === 'function') _getPrincipal = getPrincipal;
  _channel?.close();
  _channel = createLifecycleChannel({ channelFactory, getPrincipal: () => _getPrincipal() });
  return _channel;
}

/** Announce that `projectId` was deleted. No-op before configuration. */
export function postProjectDeleted(projectId) {
  return (
    _channel?.post({
      kind: LIFECYCLE_MESSAGE.PROJECT_DELETED,
      projectId,
      signal: LIFECYCLE_SIGNAL_DELETED
    }) || false
  );
}

/** Announce that this account signed out. No-op before configuration. */
export function postSignedOut() {
  return _channel?.post({ kind: LIFECYCLE_MESSAGE.SIGNED_OUT }) || false;
}

/** @param {(msg: object) => void} fn @returns {() => void} unsubscribe */
export function subscribeLifecycle(fn) {
  return _channel?.subscribe(fn) || (() => {});
}

/** Test seam — module state that would otherwise leak between cases. */
export function _resetLifecycleChannelForTesting() {
  _channel?.close();
  _channel = null;
  _getPrincipal = () => null;
}
